import { RootState } from '@/app/store';
import { camelizeNestedKeys } from '@cex/shared-modules';
import { EntityState, createEntityAdapter, createSelector } from '@reduxjs/toolkit';
import { widgetBaseApi } from '@/features/widgetBaseApi/widgetBaseApi';

// TODO: Type this
type MarketChart = {
  data: number[];
  updatedAt: number;
};
type MarketChartEntity = {
  data: number[];
  updatedAt: number;
  market: string;
};

export const marketChartAdapter = createEntityAdapter<MarketChartEntity>({
  selectId: ({ market }) => market
});

const initialState = marketChartAdapter.getInitialState();

export const marketChartApi = widgetBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    getMarketChart: builder.query<EntityState<MarketChart>, string>({
      query: (marketId) => `api/charts?market=${marketId}&range=24H`,
      providesTags: (result, error, arg) => [{ type: 'MarketChart', id: arg }],
      transformResponse(response: MarketChart, error, arg) {
        const entity = marketChartAdapter.setOne(initialState, {
          ...camelizeNestedKeys(response),
          market: arg
        });
        if (!entity) {
          throw new Error(`Could not set entity in marketChartAdapter for market ${arg}`);
        }
        return entity;
      }
    })
  })
});

const selectMarketChartByCurrency = (currency: string) => {
  return marketChartApi.endpoints.getMarketChart.select(currency);
};

// The quote API is needed for fetching the latest price of an asset
// We need to fetch the quote for each asset separately because of the BE API
// RTKQ doesn't support fetching multiple entities at once thats why we need to map over the selector
const selectMarketChartByCurrencyFactory = (currencies: string[]) => {
  return (state: RootState) => {
    return currencies.map((currency) => selectMarketChartByCurrency(currency)(state));
  };
};

export const selectAllMarketChart = (currencies: string[], state: RootState) =>
  createSelector(selectMarketChartByCurrencyFactory(currencies), (markets) =>
    markets.map((currency) => {
      if (!currency.data) return null;
      const marketId = currency.data?.ids[0];
      return currency.data?.entities[marketId];
    })
  )(state);

export const { useGetMarketChartQuery } = marketChartApi;
